import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Wine, Leaf, ForkKnife, Info, Star, Thermometer, Droplets, Mountains } from '@phosphor-icons/react';
import { Breadcrumb } from '../Product/Breadcrumb';

// Import all subregion data functions
import { getCoteRotieData } from '../../data/subregions/cote-rotie';
import { getCondrieuData } from '../../data/subregions/condrieu';
import { getHermitageData } from '../../data/subregions/hermitage';
import { getSaintJosephData } from '../../data/subregions/saint-joseph';
import { getCornasData } from '../../data/subregions/cornas';
import { getChateauneufDuPapeData } from '../../data/subregions/chateauneuf-du-pape';
import { getGigondasData } from '../../data/subregions/gigondas';
import { getVacqueyrasData } from '../../data/subregions/vacqueyras';
import { getChablisData } from '../../data/subregions/chablis';
import { getCoteDeNuitsData } from '../../data/subregions/cote-de-nuits';
import { getCoteDeBeauneData } from '../../data/subregions/cote-de-beaune';
import { getCoteChalonnaiseData } from '../../data/subregions/cote-chalonnaise';
import { getMaconnaisData } from '../../data/subregions/maconnais';
import { getMedocData } from '../../data/subregions/medoc';
import { getSaintEmilionData } from '../../data/subregions/saint_emilion';
import { getGravesData } from '../../data/subregions/graves';
import { getBergeracData } from '../../data/subregions/bergerac';
import { getCahorsData } from '../../data/subregions/cahors';
import { getMadiranData } from '../../data/subregions/madiran';
import { getJuranconData } from '../../data/subregions/jurancon';
import { getArboisData } from '../../data/subregions/arbois';
import { getLEtoileData } from '../../data/subregions/l-etoile';
import { getChateauChalonData } from '../../data/subregions/chateau-chalon';
import { getBandolData } from '../../data/subregions/bandol';
import { getCassisData } from '../../data/subregions/cassis';
import { getCotesProvenceData } from '../../data/subregions/cotes_provence';
import { getRiojaAltaData } from '../../data/subregions/rioja-alta';
import { getRiberaAltaData } from '../../data/subregions/ribera-alta';
import { getCimaCorgoData } from '../../data/subregions/cima-corgo';
import { getChiantiClassicoData } from '../../data/subregions/chianti-classico';
import { getLangheData } from '../../data/subregions/langhe';
import { getValpolicellaData } from '../../data/subregions/valpolicella';
import { getBolgheriData } from '../../data/subregions/bolgheri';
import { getMaremmaData } from '../../data/subregions/maremma';
import { getSerraDaEstrelaData } from '../../data/subregions/serra-da-estrela';
import { getAlentejoCentralData } from '../../data/subregions/alentejo-central';
import { getMoncaoMelgacoData } from '../../data/subregions/moncao-melgaco';
import { getWairauValleyData } from '../../data/subregions/wairau-valley';
import { getMartinboroughData } from '../../data/subregions/martinborough';
import { getWaiparaValleyData } from '../../data/subregions/waipara-valley';
import { getBannockburnData } from '../../data/subregions/bannockburn';
import { getGimblettGravelsData } from '../../data/subregions/gimblett-gravels';
import { getTasmanBayData } from '../../data/subregions/tasman-bay';
import { getWaimeaPlainsData } from '../../data/subregions/waimea-plains';
import { getMoutereHillsData } from '../../data/subregions/moutere-hills';
import { getCentralCanterburyData } from '../../data/subregions/central-canterbury';
import { getNorthCanterburyData } from '../../data/subregions/north-canterbury';
import { getMastertonData } from '../../data/subregions/masterton';
import { getGladstoneData } from '../../data/subregions/gladstone';
import { getEdenValleyData } from '../../data/subregions/eden-valley';
import { getHighEdenData } from '../../data/subregions/high-eden';
import { getLenswoodData } from '../../data/subregions/lenswood';
import { getPenolaData } from '../../data/subregions/penola';
import { getClareData } from '../../data/subregions/clare';
import { getUpperYarraValleyData } from '../../data/subregions/upper-yarra-valley';
import { getBlewittSpringsData } from '../../data/subregions/blewitt-springs';
import { getWilyabrupData } from '../../data/subregions/wilyabrup';
import { getLowerHunterValleyData } from '../../data/subregions/lower-hunter-valley';
import { getMauerData } from '../../data/subregions/mauer';
import { getBisambergData } from '../../data/subregions/bisamberg';
import { getNussbergData } from '../../data/subregions/nussberg';
import { getOststeiermarkData } from '../../data/subregions/oststeiermark';
import { getWeststeiermarkData } from '../../data/subregions/weststeiermark';
import { getSudsteiermarkData } from '../../data/subregions/sudsteiermark';

// Import region data functions
import { getRhoneData } from '../../data/regions/rhone';
import { getBourgogneData } from '../../data/regions/bourgogne';
import { getBordeauxData } from '../../data/regions/bordeaux';
import { getSudOuestData } from '../../data/regions/sud-ouest';
import { getLanguedocRoussillonData } from '../../data/regions/languedoc-roussillon';
import { getProvenceData } from '../../data/regions/provence';
import { getAlsaceData } from '../../data/regions/alsace';
import { getLoireData } from '../../data/regions/loire';
import { getJuraData } from '../../data/regions/jura';
import { getChampagneData } from '../../data/regions/champagne';
import { getRiojaData } from '../../data/regions/rioja';
import { getRiberaDelDueroData } from '../../data/regions/ribera-del-duero';
import { getRiasBaixasData } from '../../data/regions/rias-baixas';
import { getRuedaData } from '../../data/regions/rueda';
import { getPrioratData } from '../../data/regions/priorat';
import { getNavarraData } from '../../data/regions/navarra';
import { getCavaData } from '../../data/regions/cava';
import { getJerezData } from '../../data/regions/jerez';
import { getPiemontData } from '../../data/regions/piemont';
import { getToscaneData } from '../../data/regions/toscane';
import { getVenetieData } from '../../data/regions/venetie';
import { getEmilieRomagneData } from '../../data/regions/emilie-romagne';
import { getLombardieData } from '../../data/regions/lombardie';
import { getFrioulVenetieJulienneData } from '../../data/regions/frioul-venetie-julienne';
import { getMarchesData } from '../../data/regions/marches';
import { getAbruzzesData } from '../../data/regions/abruzzes';
import { getCampanieData } from '../../data/regions/campanie';
import { getPouillesData } from '../../data/regions/pouilles';
import { getSicileData } from '../../data/regions/sicile';
import { getSuperTuscanData } from '../../data/regions/supertuscan';
import { getDouroData } from '../../data/regions/douro';
import { getDaoData } from '../../data/regions/dao';
import { getAlentejoData } from '../../data/regions/alentejo';
import { getBairradaData } from '../../data/regions/bairrada';
import { getVinhoVerdeData } from '../../data/regions/vinho-verde';
import { getLisboaData } from '../../data/regions/lisboa';
import { getSetubalData } from '../../data/regions/setubal';
import { getMadeiraData } from '../../data/regions/madeira';
import { getMarlboroughData as getMarlboroughRegionData } from '../../data/regions/marlborough';
import { getHawkesBayData } from '../../data/regions/hawkes-bay';
import { getWairarapaData } from '../../data/regions/wairarapa';
import { getCentralOtagoData } from '../../data/regions/central-otago';
import { getNelsonData } from '../../data/regions/nelson';
import { getCanterburyData } from '../../data/regions/canterbury';
import { getTasmanieData } from '../../data/regions/tasmanie';
import { getYarraValleyData } from '../../data/regions/yarra-valley';
import { getMorningtonPeninsulaData } from '../../data/regions/mornington-peninsula';
import { getAdelaideHillsData } from '../../data/regions/adelaide-hills';
import { getBarossaValleyData } from '../../data/regions/barossa-valley';
import { getCoonawarraData } from '../../data/regions/coonawarra';
import { getClareValleyData } from '../../data/regions/clare-valley';
import { getMcLarenValeData } from '../../data/regions/mclaren-vale';
import { getMargaretRiverData } from '../../data/regions/margaret-river';
import { getHunterValleyData } from '../../data/regions/hunter-valley';
import { getSouthEasternAustraliaData } from '../../data/regions/south-eastern-australia';
import { getAutresAustralieData } from '../../data/regions/autres-australie';
import { getMendozaData } from '../../data/regions/mendoza';
import { getBajaCaliforniaData } from '../../data/regions/baja-california';
import { getCoahuilaData } from '../../data/regions/coahuila';
import { getQueretaroData } from '../../data/regions/queretaro';
import { getNiederoesterreichData } from '../../data/regions/niederoesterreich';
import { getBurgenlandData } from '../../data/regions/burgenland';
import { getSteiermarkData } from '../../data/regions/steiermark';
import { getWienData } from '../../data/regions/wien';
import { getCreteData } from '../../data/regions/crete';
import { getPeloponneseData } from '../../data/regions/peloponnese';
import { getMacedoniaData } from '../../data/regions/macedonia';

// Create a mapping of subregion functions
const subregionFunctions = {
  'cote-rotie': getCoteRotieData,
  'condrieu': getCondrieuData,
  'hermitage': getHermitageData,
  'saint-joseph': getSaintJosephData,
  'cornas': getCornasData,
  'chateauneuf-du-pape': getChateauneufDuPapeData,
  'gigondas': getGigondasData,
  'vacqueyras': getVacqueyrasData,
  'chablis': getChablisData,
  'cote-de-nuits': getCoteDeNuitsData,
  'cote-de-beaune': getCoteDeBeauneData,
  'cote-chalonnaise': getCoteChalonnaiseData,
  'maconnais': getMaconnaisData,
  'medoc': getMedocData,
  'saint_emilion': getSaintEmilionData,
  'graves': getGravesData,
  'bergerac': getBergeracData,
  'cahors': getCahorsData,
  'madiran': getMadiranData,
  'jurancon': getJuranconData,
  'arbois': getArboisData,
  'l-etoile': getLEtoileData,
  'chateau-chalon': getChateauChalonData,
  'bandol': getBandolData,
  'cassis': getCassisData,
  'cotes_provence': getCotesProvenceData,
  'rioja-alta': getRiojaAltaData,
  'ribera-alta': getRiberaAltaData,
  'cima-corgo': getCimaCorgoData,
  'chianti-classico': getChiantiClassicoData,
  'langhe': getLangheData,
  'valpolicella': getValpolicellaData,
  'bolgheri': getBolgheriData,
  'maremma': getMaremmaData,
  'serra-da-estrela': getSerraDaEstrelaData,
  'alentejo-central': getAlentejoCentralData,
  'moncao-melgaco': getMoncaoMelgacoData,
  'wairau-valley': getWairauValleyData,
  'martinborough': getMartinboroughData,
  'waipara-valley': getWaiparaValleyData,
  'bannockburn': getBannockburnData,
  'gimblett-gravels': getGimblettGravelsData,
  'tasman-bay': getTasmanBayData,
  'waimea-plains': getWaimeaPlainsData,
  'moutere-hills': getMoutereHillsData,
  'central-canterbury': getCentralCanterburyData,
  'north-canterbury': getNorthCanterburyData,
  'masterton': getMastertonData,
  'gladstone': getGladstoneData,
  'eden-valley': getEdenValleyData,
  'high-eden': getHighEdenData,
  'lenswood': getLenswoodData,
  'penola': getPenolaData,
  'clare': getClareData,
  'upper-yarra-valley': getUpperYarraValleyData,
  'blewitt-springs': getBlewittSpringsData,
  'wilyabrup': getWilyabrupData,
  'lower-hunter-valley': getLowerHunterValleyData,
  'mauer': getMauerData,
  'bisamberg': getBisambergData,
  'nussberg': getNussbergData,
  'oststeiermark': getOststeiermarkData,
  'weststeiermark': getWeststeiermarkData,
  'sudsteiermark': getSudsteiermarkData
};

// Create a mapping of region functions
const regionFunctions = {
  'rhone': getRhoneData,
  'bourgogne': getBourgogneData,
  'bordeaux': getBordeauxData,
  'sud-ouest': getSudOuestData,
  'languedoc-roussillon': getLanguedocRoussillonData,
  'provence': getProvenceData,
  'alsace': getAlsaceData,
  'loire': getLoireData,
  'jura': getJuraData,
  'champagne': getChampagneData,
  'rioja': getRiojaData,
  'ribera-del-duero': getRiberaDelDueroData,
  'rias-baixas': getRiasBaixasData,
  'rueda': getRuedaData,
  'priorat': getPrioratData,
  'navarra': getNavarraData,
  'cava': getCavaData,
  'jerez': getJerezData,
  'piemont': getPiemontData,
  'toscane': getToscaneData,
  'venetie': getVenetieData,
  'emilie-romagne': getEmilieRomagneData,
  'lombardie': getLombardieData,
  'frioul-venetie-julienne': getFrioulVenetieJulienneData,
  'marches': getMarchesData,
  'abruzzes': getAbruzzesData,
  'campanie': getCampanieData,
  'pouilles': getPouillesData,
  'sicile': getSicileData,
  'supertuscan': getSuperTuscanData,
  'douro': getDouroData,
  'dao': getDaoData,
  'alentejo': getAlentejoData,
  'bairrada': getBairradaData,
  'vinho-verde': getVinhoVerdeData,
  'lisboa': getLisboaData,
  'setubal': getSetubalData,
  'madeira': getMadeiraData,
  'marlborough': getMarlboroughRegionData,
  'hawkes-bay': getHawkesBayData,
  'wairarapa': getWairarapaData,
  'central-otago': getCentralOtagoData,
  'nelson': getNelsonData,
  'canterbury': getCanterburyData,
  'tasmanie': getTasmanieData,
  'yarra-valley': getYarraValleyData,
  'mornington-peninsula': getMorningtonPeninsulaData,
  'adelaide-hills': getAdelaideHillsData,
  'barossa-valley': getBarossaValleyData,
  'coonawarra': getCoonawarraData,
  'clare-valley': getClareValleyData,
  'mclaren-vale': getMcLarenValeData,
  'margaret-river': getMargaretRiverData,
  'hunter-valley': getHunterValleyData,
  'south-eastern-australia': getSouthEasternAustraliaData,
  'autres-australie': getAutresAustralieData,
  'mendoza': getMendozaData,
  'baja-california': getBajaCaliforniaData,
  'coahuila': getCoahuilaData,
  'queretaro': getQueretaroData,
  'niederoesterreich': getNiederoesterreichData,
  'burgenland': getBurgenlandData,
  'steiermark': getSteiermarkData,
  'wien': getWienData,
  'crete': getCreteData,
  'peloponnese': getPeloponneseData,
  'macedonia': getMacedoniaData
};

export const SubregionsListPage = () => {
  const { country, region } = useParams();
  const [regionData, setRegionData] = useState(null);
  const [subregions, setSubregions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = () => {
      try {
        // Get region data
        const getRegionData = regionFunctions[region];
        if (!getRegionData) {
          setError('Région non trouvée');
          setLoading(false);
          return;
        }

        const data = getRegionData();
        setRegionData(data);

        // Get subregions from region data
        if (data.subregions) {
          const subregionList = Object.entries(data.subregions).map(([key, subregion]) => {
            // Try to get detailed subregion data if available
            const getSubregionData = subregionFunctions[key];
            if (getSubregionData) {
              const detailedData = getSubregionData();
              return {
                key,
                name: detailedData.name,
                description: detailedData.description,
                area: detailedData.area,
                production: detailedData.production,
                wineStyles: detailedData.wineStyles,
                grapeVarieties: detailedData.grapeVarieties,
                characteristics: detailedData.characteristics
              };
            } else {
              // Use basic data from region file
              return {
                key,
                name: subregion.name,
                description: subregion.description,
                characteristics: subregion.characteristics,
                grapeVarieties: subregion.grapeVarieties,
                wineStyles: subregion.wineStyles
              };
            }
          });
          setSubregions(subregionList);
        } else {
          setSubregions([]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading region data:', error);
        setError('Erreur lors du chargement des données.');
        setLoading(false);
      }
    };

    loadData();
  }, [region]);

  if (loading) {
    return <main style={{ padding: 40, textAlign: 'center' }}>Chargement...</main>;
  }

  if (error) {
    return (
      <main style={{ padding: 40, textAlign: 'center' }}>
        <h1>Erreur</h1>
        <p>{error}</p>
        <Link
          to={`/info/countries/${country}/regions/${region}`}
          style={{
            background: '#4B164C',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            textDecoration: 'none',
            display: 'inline-block',
            marginTop: '16px'
          }}
        >Retour à la région</Link>
      </main>
    );
  }

  if (!regionData) return null;

  return (
    <main style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Breadcrumb />
      
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px solid #e8e0e8' }}>
        <Link
          to={`/info/countries/${country}/regions/${region}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
          onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
          onMouseOut={e => e.currentTarget.style.background = 'none'}
        >
          <ArrowLeft size={24} color="#4B164C" />
        </Link>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '32px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '700' }}>
            Sous-régions de {regionData.name}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px' }}>
            <MapPin size={16} />
            <span>{regionData.name} - {country}</span>
          </div>
        </div>
      </div>

      {/* Subregions Grid */}
      {subregions.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px' }}>
          {subregions.map((subregion) => (
            <Link
              key={subregion.key}
              to={`/info/countries/${country}/regions/${region}/${subregion.key}`}
              style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 4px 24px rgba(75,22,76,0.08)',
                textDecoration: 'none',
                color: 'inherit',
                transition: 'transform 0.2s, box-shadow 0.2s',
                display: 'block'
              }}
              onMouseOver={e => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(75,22,76,0.12)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(75,22,76,0.08)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: '#4B164C',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px'
                }}>
                  🍇
                </div>
                <div>
                  <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '600' }}>
                    {subregion.name}
                  </h3>
                  {subregion.area && (
                    <span style={{
                      background: '#f0e6f0',
                      color: '#4B164C',
                      padding: '4px 8px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {subregion.area}
                    </span>
                  )}
                </div>
              </div>
              
              <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5', marginBottom: '16px' }}>
                {subregion.description}
              </p>

              {/* Wine Styles */}
              {subregion.wineStyles && (
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '14px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Wine size={16} />
                    Styles de vin
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {Object.entries(subregion.wineStyles).map(([style, description]) => (
                      <span key={style} style={{
                        background: '#f8f6fa',
                        color: '#4B164C',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        border: '1px solid #e8e0e8'
                      }}>
                        {style}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Grape Varieties */}
              {subregion.grapeVarieties && (
                <div>
                  <h4 style={{ fontSize: '14px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Leaf size={16} />
                    Cépages principaux
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {Object.entries(subregion.grapeVarieties).map(([color, varieties]) => (
                      <div key={color} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {Array.isArray(varieties) ? varieties.slice(0, 3).map((variety, index) => (
                          <span key={index} style={{
                            background: color === 'red' ? '#fef2f2' : '#f0f9ff',
                            color: color === 'red' ? '#dc2626' : '#0369a1',
                            padding: '3px 6px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            {variety}
                          </span>
                        )) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Link>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: '#f8f6fa',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            color: '#999'
          }}>
            <Info size={32} />
          </div>
          <h3 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 12px 0', fontWeight: '600' }}>
            Aucune sous-région trouvée
          </h3>
          <p style={{ fontSize: '16px', color: '#666', margin: '0 0 24px 0' }}>
            Cette région ne possède pas de sous-régions définies.
          </p>
          <Link
            to={`/info/countries/${country}/regions/${region}`}
            style={{
              background: '#4B164C',
              color: '#fff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              textDecoration: 'none',
              display: 'inline-block'
            }}
          >Retour à la région</Link>
        </div>
      )}
    </main>
  );
}; 